import React from 'react';

function TravelField({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide leading-none mb-1">{label}</p>
      <p className="text-sm font-medium text-gray-800 leading-snug">{value}</p>
    </div>
  );
}

const DEFAULT_SECTION_ORDER = ['greeting', 'travel_data', 'photo', 'why', 'inclusions', 'closing', 'cta'];

export default function ProposalRendererV2({ proposal_v2, agentSlug }) {
  if (!proposal_v2) return null;

  const {
    travel_data: td = {},
    persuasive: p   = {},
    media           = {},
    cta             = {},
    branding_snapshot: bs = {},
    meta            = {},
  } = proposal_v2;

  const showPhoto   = media.photo_visible && !!media.photo_url;
  const showWhy     = (p.why_bullets || []).length > 0;
  const showInc     = (p.inclusions  || []).length > 0;
  const showClosing = !!p.closing?.trim();
  const showCta     = cta.visible && (cta.url || agentSlug);
  const ctaHref     = cta.url || (agentSlug ? `/book/${agentSlug}` : '#');

  const travelFields = [
    ['Hotel',        td.hotel_name ? `${td.hotel_name}${td.hotel_stars ? ` · ${td.hotel_stars}★` : ''}` : null],
    ['Kamertype',    td.room_type],
    ['Vertrekdatum', td.departure_date],
    ['Terugkomst',   td.return_date],
    ['Duur',         td.duration_nights ? `${td.duration_nights} nachten` : null],
    ['Luchthaven',   td.departure_airport],
    ['Aankomst',     td.arrival_city],
    ['Catering',     td.board_type],
    ['Vlucht',       td.flight_info],
  ].filter(([, v]) => v);

  const SECTION_BLOCKS = {
    greeting: p.greeting ? (
      <p key="greeting" className="text-gray-600 text-sm leading-relaxed">{p.greeting}</p>
    ) : null,

    travel_data: (
      <div key="travel_data" className="rounded-lg overflow-hidden border border-gray-200">
        {travelFields.length > 0 && (
          <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-4">
            {travelFields.map(([label, value]) => (
              <TravelField key={label} label={label} value={value} />
            ))}
          </div>
        )}
        {(td.price_per_person || td.price_total) && (
          <div
            className="px-5 py-4 flex items-baseline gap-6"
            style={{ backgroundColor: 'var(--brand-light, #eff6ff)', borderTop: travelFields.length > 0 ? '1px solid #e5e7eb' : 'none' }}
          >
            {td.price_per_person && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Prijs per persoon</p>
                <p className="text-2xl font-semibold text-gray-900">€{td.price_per_person}</p>
              </div>
            )}
            {td.price_total && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Totaal</p>
                <p className="text-lg font-medium text-gray-500">€{td.price_total}</p>
              </div>
            )}
          </div>
        )}
        {travelFields.length === 0 && !td.price_per_person && !td.price_total && (
          <div className="p-5">
            <p className="text-sm text-gray-400">Reisdetails worden weergegeven zodra ze zijn ingevuld.</p>
          </div>
        )}
        {meta.include_source_url && meta.source_url && (
          <div className="px-5 py-4 border-t border-gray-200">
            <a
              href={meta.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-lg border-2 text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ borderColor: 'var(--brand)', color: 'var(--brand)', backgroundColor: 'transparent' }}
            >
              🔗 Bekijk het volledige aanbod online
            </a>
          </div>
        )}
      </div>
    ),

    photo: showPhoto ? (
      <div key="photo" className="rounded-lg overflow-hidden">
        <img
          src={media.photo_url}
          alt={td.destination || ''}
          className="w-full h-48 object-cover"
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>
    ) : null,

    why: showWhy ? (
      <div key="why">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Waarom deze reis?</h3>
        {p.why_intro && (
          <p className="text-sm text-gray-500 mb-3 leading-relaxed">{p.why_intro}</p>
        )}
        <ul className="space-y-2">
          {p.why_bullets.slice(0, 5).map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700 leading-snug">
              <span className="mt-0.5 shrink-0 font-semibold text-xs" style={{ color: 'var(--brand)' }}>✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null,

    inclusions: showInc ? (
      <div key="inclusions">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Inbegrepen</h3>
        <div className="flex flex-wrap gap-1.5">
          {p.inclusions.slice(0, 8).map((inc, i) => (
            <span key={i} className="text-xs px-3 py-1 rounded-full border border-gray-200 text-gray-600 bg-white">
              ✓ {inc}
            </span>
          ))}
        </div>
      </div>
    ) : null,

    closing: showClosing ? (
      <p key="closing" className="text-sm text-gray-500 italic leading-relaxed border-l-2 border-gray-200 pl-4">
        {p.closing}
      </p>
    ) : null,

    cta: showCta ? (
      <div key="cta">
        <a
          href={ctaHref}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--brand)' }}
        >
          {cta.text || 'Plan een afspraak'}
        </a>
        {cta.note && (
          <p className="text-xs text-gray-400 mt-1.5">{cta.note}</p>
        )}
      </div>
    ) : null,
  };

  // Respect user-defined section order, fall back to default, skip 'ai' (editor-only)
  const order = (proposal_v2.section_order || DEFAULT_SECTION_ORDER)
    .filter(k => DEFAULT_SECTION_ORDER.includes(k));

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 max-w-2xl mx-auto">

      {/* Fixed header — always first */}
      <div className="px-8 py-8" style={{ backgroundColor: 'var(--brand)' }}>
        <h1 className="text-[22px] font-semibold text-white leading-tight tracking-tight">
          {td.destination || '—'}
        </h1>
        {td.hotel_name && (
          <p className="text-sm font-normal text-white/75 mt-1">
            {td.hotel_name}{td.hotel_stars ? ` · ${td.hotel_stars}★` : ''}
          </p>
        )}
        {p.tagline && (
          <p className="text-white/60 italic text-sm mt-3 leading-relaxed">{p.tagline}</p>
        )}
        {bs.agency_name && (
          <p className="text-white/35 text-xs mt-4 font-normal">{bs.agency_name}</p>
        )}
      </div>

      {/* Ordered sections */}
      <div className="px-8 py-7 space-y-7">
        {order.map(key => SECTION_BLOCKS[key] || null)}

        {/* Fixed branding footer — always last */}
        <div className="pt-5 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-2">
              {bs.agency_name && <span className="font-medium text-gray-500">{bs.agency_name}</span>}
              {bs.agent_name  && <span className="text-gray-400">· {bs.agent_name}</span>}
            </div>
            <div className="flex gap-4">
              {bs.phone       && <span>📞 {bs.phone}</span>}
              {bs.website_url && <span>🌐 {bs.website_url}</span>}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
